/**
 * First-run onboarding wizard — multi-step overlay for new users.
 * Steps: Welcome -> Camera Permission -> Ready.
 */

import { useState, useCallback } from 'react';

interface OnboardingWizardProps {
  onComplete: () => void;
}

const TOTAL_STEPS = 3;

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [cameraGranted, setCameraGranted] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const handleNext = useCallback(() => {
    if (step < TOTAL_STEPS - 1) {
      setStep((s) => s + 1);
    } else {
      onComplete();
    }
  }, [step, onComplete]);

  const handleBack = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const handleRequestCamera = useCallback(async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      // Release the stream immediately — we just needed permission
      stream.getTracks().forEach((t) => t.stop());
      setCameraGranted(true);
      // Auto-advance to next step after a short delay
      setTimeout(() => setStep(2), 500);
    } catch (err) {
      setCameraError(err instanceof Error ? err.message : 'Camera permission denied');
    }
  }, []);

  return (
    <div
      data-testid="onboarding-wizard"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm"
    >
      <div className="w-full max-w-md mx-4 flex flex-col items-center text-center px-6 py-10">
        {/* Step content */}
        {step === 0 && <WelcomeStep />}
        {step === 1 && (
          <CameraStep granted={cameraGranted} error={cameraError} onRequest={handleRequestCamera} />
        )}
        {step === 2 && <ReadyStep />}

        {/* Navigation */}
        <div className="mt-10 flex items-center gap-4 w-full max-w-xs">
          {step > 0 && (
            <button
              type="button"
              onClick={handleBack}
              className="rounded-lg border border-border px-4 py-2.5 text-sm text-text-secondary hover:bg-surface transition-colors"
            >
              Back
            </button>
          )}

          <button
            type="button"
            onClick={handleNext}
            className="flex-1 rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-white hover:bg-accent-hover transition-colors"
            data-testid="onboarding-next"
          >
            {step === TOTAL_STEPS - 1 ? "Let's go!" : 'Next'}
          </button>
        </div>

        {/* Skip */}
        <button
          type="button"
          onClick={onComplete}
          className="mt-4 text-xs text-text-secondary/60 hover:text-text-secondary transition-colors"
          data-testid="onboarding-skip"
        >
          Skip
        </button>

        {/* Step dots */}
        <div className="mt-6 flex gap-2" aria-label={`Step ${step + 1} of ${TOTAL_STEPS}`}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-6 bg-accent' : i < step ? 'w-1.5 bg-accent/40' : 'w-1.5 bg-border'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Step Components ────────────────────────────────────────────

function WelcomeStep() {
  return (
    <>
      {/* Box icon */}
      <div className="mb-6 text-accent" aria-hidden="true">
        <svg
          className="h-20 w-20"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-text-primary">Welcome to Peekbox</h1>
      <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-xs">
        Know what&apos;s in every box. Scan ArUco markers or QR codes with your camera to instantly
        see and manage the contents of your storage boxes.
      </p>
    </>
  );
}

interface CameraStepProps {
  granted: boolean;
  error: string | null;
  onRequest: () => void;
}

function CameraStep({ granted, error, onRequest }: CameraStepProps) {
  return (
    <>
      {/* Camera icon */}
      <div className={`mb-6 ${granted ? 'text-success' : 'text-accent'}`} aria-hidden="true">
        <svg
          className="h-20 w-20"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          {granted ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
            />
          )}
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-text-primary">
        {granted ? 'Camera Ready!' : 'Camera Access'}
      </h1>
      <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-xs">
        {granted
          ? 'Camera permission granted. You can now scan markers.'
          : 'Peekbox needs camera access to scan ArUco markers and QR codes on your boxes. Your camera feed stays on-device and is never uploaded.'}
      </p>

      {!granted && (
        <button
          type="button"
          onClick={onRequest}
          data-testid="onboarding-camera-btn"
          className="mt-5 rounded-lg bg-accent/10 border border-accent/30 px-5 py-2.5 text-sm font-semibold text-accent hover:bg-accent/20 transition-colors"
        >
          Allow Camera
        </button>
      )}

      {error && (
        <p className="mt-3 text-xs text-danger" data-testid="onboarding-camera-error">
          {error}
        </p>
      )}
    </>
  );
}

function ReadyStep() {
  return (
    <>
      {/* Checkmark icon */}
      <div className="mb-6 text-success" aria-hidden="true">
        <svg
          className="h-20 w-20"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-text-primary">You&apos;re All Set!</h1>
      <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-xs">
        Point your camera at any ArUco marker to see what&apos;s inside the box. You can also browse
        and search your boxes from the Boxes tab.
      </p>
    </>
  );
}
